import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Divider,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

const ACTION_WIDTH = "16%";
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

const platformColor = (platform) => {
  switch (String(platform || "").toLowerCase()) {
    case "resto":
      return "info";
    case "retail":
      return "success";
    default:
      return "secondary";
  }
};

// Client IDs are UUIDs; show enough to recognise a row without exposing the whole
// credential. The detail view has an explicit reveal toggle for the full value.
const maskClientId = (v) => (!v ? "—" : `${v.slice(0, 8)}…${v.slice(-4)}`);

function HeadCell({ children, align, width }) {
  return (
    <TableCell align={align} sx={{ width, px: 2, py: 1.5 }}>
      <MDTypography
        variant="button"
        color="white"
        fontWeight="bold"
        sx={{ fontSize: 13, letterSpacing: "0.02em", textTransform: "none" }}
      >
        {children}
      </MDTypography>
    </TableCell>
  );
}

HeadCell.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.string,
  width: PropTypes.string,
};

HeadCell.defaultProps = { align: "left", width: undefined };

export default function MerchantTable({
  rows,
  columns,
  onView,
  loading,
  total,
  defaultRowsPerPage,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.merchantName, r.internalMerchantId, r.platform].some((f) =>
        String(f || "").toLowerCase().includes(q)
      )
    );
  }, [rows, query]);

  const pageRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const renderCell = (row, column) => {
    const value = row[column.key];

    if (column.key === "platform") {
      return value ? (
        <MDBadge
          badgeContent={value}
          color={platformColor(value)}
          variant="contained"
          size="xs"
          container
        />
      ) : (
        <MDTypography variant="button" color="text" sx={{ fontSize: 14 }}>
          —
        </MDTypography>
      );
    }

    if (column.key === "clientId") {
      return (
        <MDTypography
          variant="button"
          color="text"
          sx={{ fontFamily: MONO, fontSize: 13, textTransform: "none", fontWeight: 400 }}
        >
          {maskClientId(value)}
        </MDTypography>
      );
    }

    const isPrimary = column.key === "merchantName";
    return (
      <MDTypography
        variant="button"
        color={isPrimary ? "dark" : "text"}
        title={typeof value === "string" ? value : undefined}
        sx={{
          fontSize: 14,
          fontWeight: isPrimary ? 500 : 400,
          textTransform: "none",
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </MDTypography>
    );
  };

  const showingFrom = filtered.length === 0 ? 0 : page * rowsPerPage + 1;
  const showingTo = Math.min(filtered.length, (page + 1) * rowsPerPage);

  return (
    <Card sx={{ overflow: "hidden" }}>
      {/* Card header: title, count, search */}
      <MDBox
        px={3}
        pt={2.5}
        pb={2}
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <MDBox>
          <MDTypography variant="h6" color="dark" fontWeight="medium">
            Registered merchants
          </MDTypography>
          <MDTypography variant="button" color="text" sx={{ textTransform: "none" }}>
            {loading
              ? "Loading…"
              : `${filtered.length} of ${Number.isFinite(total) ? total : rows.length} ${
                  (Number.isFinite(total) ? total : rows.length) === 1 ? "merchant" : "merchants"
                }`}
          </MDTypography>
        </MDBox>

        <TextField
          size="small"
          placeholder="Search merchants"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.main" }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: "100%", sm: 260 } }}
          inputProps={{ "aria-label": "Search merchants" }}
        />
      </MDBox>

      {/* Below the min width the fixed columns would crush, so the container scrolls instead. */}
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table sx={{ width: "100%", minWidth: 760, tableLayout: "fixed" }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} />
            ))}
            <col style={{ width: ACTION_WIDTH }} />
          </colgroup>

          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow sx={{ "& th": { bgcolor: "info.main", borderBottom: "none" } }}>
              {columns.map((c) => (
                <HeadCell key={c.key} align={c.align} width={c.width}>
                  {c.label}
                </HeadCell>
              ))}
              <HeadCell align="right" width={ACTION_WIDTH}>
                Action
              </HeadCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading &&
              Array.from({ length: rowsPerPage > 5 ? 5 : rowsPerPage }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} sx={{ px: 2, py: 1.5 }}>
                      <Skeleton variant="text" width="70%" height={20} />
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ px: 2, py: 1.5 }}>
                    <Skeleton variant="text" width={80} height={20} sx={{ ml: "auto" }} />
                  </TableCell>
                </TableRow>
              ))}

            {!loading &&
              pageRows.map((row) => (
                <TableRow
                  hover
                  key={row.clientId || row.internalMerchantId}
                  sx={{
                    "& td": { px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "grey.200" },
                    "&:last-of-type td": { borderBottom: "none" },
                  }}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align} sx={{ width: c.width }}>
                      {renderCell(row, c)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: ACTION_WIDTH }}>
                    <MDButton
                      variant="text"
                      color="info"
                      size="small"
                      onClick={() => onView(row)}
                      sx={{ fontWeight: 600, fontSize: 13, textTransform: "none" }}
                    >
                      View
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}

            {!loading && pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={{ borderBottom: "none" }}>
                  <MDBox py={6} textAlign="center">
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      {query ? "No merchants match that search" : "No merchants registered yet"}
                    </MDTypography>
                    <MDTypography
                      variant="button"
                      color="text"
                      sx={{ textTransform: "none", display: "block", mt: 0.5 }}
                    >
                      {query
                        ? "Try a different name, internal ID, or platform."
                        : "Merchants appear here once they register with the middleware."}
                    </MDTypography>
                  </MDBox>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filtered.length > 0 && (
        <>
          <Divider sx={{ m: 0 }} />
          <MDBox
            px={3}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
          >
            <MDTypography variant="button" color="text" sx={{ textTransform: "none" }}>
              Showing {showingFrom}–{showingTo} of {filtered.length}
            </MDTypography>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_e, next) => setPage(next)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows"
              sx={{
                borderBottom: "none",
                "& .MuiTablePagination-toolbar": { px: 0 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  m: 0,
                  fontSize: 13,
                  color: "text.main",
                },
              }}
            />
          </MDBox>
        </>
      )}
    </Card>
  );
}

const columnShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.string,
  align: PropTypes.string,
});

MerchantTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      clientId: PropTypes.string,
      merchantName: PropTypes.string,
      internalMerchantId: PropTypes.string,
      platform: PropTypes.string,
    })
  ).isRequired,
  columns: PropTypes.arrayOf(columnShape).isRequired,
  onView: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  total: PropTypes.number,
  defaultRowsPerPage: PropTypes.number,
};

MerchantTable.defaultProps = {
  loading: false,
  total: undefined,
  defaultRowsPerPage: 10,
};
